<template>
  <VueFinalModal v-if="displayPlayer" overlay-transition="vfm-fade" content-transition="vfm-fade">
    <HandshakeSheet :dimmed="false" keep-centre @close="handleDialogClose">
      <template #top>
        <div v-if="isStalled || isDeleting" class="message">{{ localize('handshake.loading') }}</div>

        <div v-else class="message">
          <HandshakePairFaces
            :other-image="displayPlayer.profileImageBlobHash"
            :other-first="!!handshake && !isInitiator"
          />
          <div class="lead-row">
            <span v-if="isWaitingForConfirmation && !isInitiator">{{ localize('handshake.offered.lead') }}</span>
            <span class="line player-name">@{{ displayPlayer.username }}</span>
          </div>
        </div>
      </template>

      <!-- The disc is the sheet, so a request that has stalled greys it where it
           stands rather than putting a spinner somewhere else. -->
      <div v-if="isStalled || isDeleting" class="handshake-disc skeleton" />

      <!-- A finger taps the disc three times, then rests, until the tap has been
           made once. Words under it were read as a status, not an invitation. -->
      <div v-else class="handshake-button" @click="handleSubmit">
        <div class="handshake-disc" :class="{ 'is-waiting': isWaiting, 'is-knocking': showTapHint }">
          <span v-if="isWaiting" class="handshake-pulse" />
          <div class="handshake-disc-icon">
            <GamIcon :name="GamIconName.HANDSHAKE" size="64px" />
          </div>
        </div>
        <span v-if="showTapHint" class="tap-hint" aria-hidden="true">
          <GamIcon :name="GamIconName.TAP" size="56px" />
        </span>
      </div>

      <!-- Decline stays beside the disc it answers. Offering closes the sheet, so a
           waiting offer is withdrawn from the list rather than here. -->
      <template v-if="isRefusable && !isDeleting && !chosenFiles.length" #footer>
        <GamButton :size="GamButtonSize.SMALL" :variant="GamButtonVariant.STEKLO" @click="showCancelConfirm = true">
          {{ localize('handshake.decline.action') }}
        </GamButton>
      </template>

      <template #bottom-left>
        <!-- Day over time, so it stays narrow beside Decline. -->
        <div v-if="showTimestamp && handshake" class="timestamp">
          <span v-for="part in GambitHelper.formatTime(handshake.createdAt)" :key="part">{{ part }}</span>
        </div>
      </template>

      <!-- What goes with the handshake, below it as on the recommendation: the
           photo, and what the two played. A note typed before there is a
           handshake to put it on waits for the one this disc makes. -->
      <!-- Kept while the handshake is sending, only resting: taking it away
           re-centres the board without its toolbar, and the sheet jumps. -->
      <template #toolbar>
        <div class="photo-slot" :class="{ 'is-busy': isBusy }" :inert="isBusy">
          <button
            type="button"
            class="photo-button"
            :class="{ 'has-photo': !!handshakeImage }"
            :aria-label="localize('handshake.photo.add')"
            @click="handleUpload"
          >
            <img v-if="handshakeImage" :src="handshakeImage" alt="" />
            <GamIcon v-else :name="GamIconName.IMAGE" size="18px" is-current />
          </button>
          <button
            v-if="previewUrl"
            type="button"
            class="photo-remove"
            :aria-label="localize('handshake.photo.remove')"
            @click="handleReset"
          >
            <GamIcon :name="GamIconName.X_CLOSE" size="10px" is-current />
          </button>
        </div>
        <div class="note-slot" :class="{ 'is-busy': isBusy }" :inert="isBusy">
          <HandshakeNoteField v-model="note" :hint="otherNote" @change="handleNoteChange" />
        </div>
      </template>
    </HandshakeSheet>

    <GamConfirmModal
      v-if="showCancelConfirm"
      :title="localize('handshake.decline.title')"
      :keep-label="localize('handshake.cancel.dismiss')"
      :destructive-label="localize('handshake.decline.confirm')"
      @close="showCancelConfirm = false"
      @keep="showCancelConfirm = false"
      @confirm="handleConfirmCancel"
    />
  </VueFinalModal>
</template>

<script setup lang="ts">
import type { HandshakeDto } from '@/core/data/handshake/handshake.interface';
import type { AttendeeDto } from '@/core/data/user/user.interface';
import { gambits, localize } from '@/core/gambits';
import GambitHelper from '@/core/helpers/gambit.helper';
import { useAuthStore } from '@/stores/auth.store';
import GamButton from '@/views/components/GamButton.vue';
import GamConfirmModal from '@/views/components/GamConfirmModal.vue';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamButtonSize, GamButtonVariant } from '@/views/composables/constants/components/gamButton.constants';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import { GamComponentsEmits } from '@/views/composables/constants/main/emit.constants';
import { useConfetti } from '@/views/composables/hooks/useConfetti';
import { useHandshakePhotoUpload } from '@/views/composables/hooks/useHandshakePhotoUpload';
import HandshakeNoteField from '@/views/main/handshakes/HandshakeNoteField.vue';
import HandshakePairFaces from '@/views/main/handshakes/HandshakePairFaces.vue';
import HandshakeSheet from '@/views/main/handshakes/HandshakeSheet.vue';
import { celebrateHandshake, rememberHandshake } from '@/views/main/handshakes/utils/handshake-celebration';
import { tidyHandshakeNote } from '@/views/main/handshakes/utils/handshake-note';
import { photosInSlot, photosToUpload } from '@/views/main/handshakes/utils/handshake-photos';
import { learnHandshakeTap, shouldShowHandshakeTap } from '@/views/main/handshakes/utils/handshake-tap-hint';
import { useFileDialog } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed, onUnmounted, ref, unref, watch, watchEffect, type Ref } from 'vue';
import { VueFinalModal } from 'vue-final-modal';
import { useToast } from 'vue-toastification';

export interface HandshakeModalProps {
  onClose: () => void;
  player: Ref<AttendeeDto | null>;
  handshake: Ref<HandshakeDto | null>;
  gambitId?: Ref<string | null>;
  locationId?: Ref<string | null>;
  showTimestamp?: Ref<boolean>;
}

const props = defineProps<HandshakeModalProps>();
const emit = defineEmits([GamComponentsEmits.CLOSE]);

const authStore = useAuthStore();
const { user, userDetails } = storeToRefs(authStore);

// Reactive unwrapping – props may arrive as raw Refs (from useModal attrs)
const handshake = computed(() => unref(props.handshake) as HandshakeDto | null);
const player = computed(() => unref(props.player) as AttendeeDto | null);
const gambitId = computed(() => handshake.value?.gambitId || (unref(props.gambitId) as string | null) || null);
const locationId = computed(() => handshake.value?.locationId || (unref(props.locationId) as string | null) || null);
const showTimestamp = computed(() => (unref(props.showTimestamp) as boolean) ?? false);
// Derive the "other player" from the handshake DTO when available,
// so the correct username is shown regardless of what the parent passes as `player`.
const displayPlayer = computed(() => {
  const h = handshake.value;
  if (h && user.value) {
    if (h.initiatorId === user.value.id && h.responder) return h.responder;
    if (h.responderId === user.value.id && h.initiator) return h.initiator;
  }
  return player.value;
});

const isSubmitting = ref(false);
const isStalled = ref(false);
const isDeleting = ref(false);
const isBusy = computed(() => isSubmitting.value || isStalled.value || isDeleting.value);
const actionTaken = ref(false);

let stallTimer: ReturnType<typeof setTimeout> | null = null;

const previewUrl = ref<string | undefined>();
const chosenFiles = ref<File[]>([]);

watch([() => unref(props.handshake), () => unref(props.player)], () => {
  stopSubmitting();
  isDeleting.value = false;
  actionTaken.value = false;
  previewUrl.value = undefined;
  chosenFiles.value = [];
  note.value = ownNote();
});

onUnmounted(() => {
  if (stallTimer) clearTimeout(stallTimer);
});

const toast = useToast();
const { fire: fireConfetti } = useConfetti();
const { upload: uploadPhoto } = useHandshakePhotoUpload();

const handshakeImage = computed(() => {
  if (previewUrl.value) {
    return previewUrl.value;
  }

  if (handshake.value?.initiatorImageBlobHash) {
    return gambits.imageService.getImageSrc(handshake.value.initiatorImageBlobHash, 288 * 2);
  }

  return undefined;
});

const isInitiator = computed(() => handshake.value && handshake.value.initiatorId === user.value?.id);
const isResponder = computed(() => handshake.value && handshake.value.responderId === user.value?.id);
const isWaitingForConfirmation = computed(() => handshake.value && !handshake.value.isConfirmed);
const isRefusable = computed(() => isWaitingForConfirmation.value && isResponder.value);

/** An offer of one's own, still out. The disc has nothing to do but wait. */
const isWaiting = computed(() => !!isWaitingForConfirmation.value && !!isInitiator.value);

/** Offering or accepting: the two states the disc is pressed in. */
const canTapDisc = computed(() => !handshake.value || !!isRefusable.value);

const showTapHint = computed(
  () =>
    canTapDisc.value &&
    !isBusy.value &&
    !actionTaken.value &&
    shouldShowHandshakeTap(userDetails.value?.handshakePartnerIds?.length ?? 0),
);

/** What this player has already said about the game, if anything. */
const ownNote = (): string => {
  const h = handshake.value;
  if (!h || !user.value) return '';

  return (h.initiatorId === user.value.id ? h.initiatorNote : h.responderNote) || '';
};

const note = ref(ownNote());

/** What the one who offered wrote, so the one accepting sees it before writing their own. */
const otherNote = computed((): string => {
  const h = handshake.value;
  if (!h || !user.value) return '';

  return (h.initiatorId === user.value.id ? h.responderNote : h.initiatorNote) || '';
});

/**
 * Whether the handshake will take the note as things stand. The other side of
 * an offer is not theirs to write on until they have taken it — the rule the
 * photo goes by as well — so until then the note is held here and goes up with
 * the accept.
 */
const canSaveNote = computed(() => !!handshake.value && (isInitiator.value || handshake.value.isConfirmed));

const saveNote = async (handshakeId: string, written: string) => {
  const result = await gambits.userService.setHandshakeNote(handshakeId, written);
  if (result.error) toast.error(result.error.message || localize('handshake.note.failed'));
};

const handleNoteChange = () => {
  if (!canSaveNote.value || !handshake.value) return;

  void saveNote(handshake.value.id, tidyHandshakeNote(note.value));
};

const {
  files: uploadFiles,
  open: openUpload,
  reset: resetUpload,
} = useFileDialog({ multiple: true, accept: 'image/*' });

watchEffect(() => {
  const picked = uploadFiles.value;
  if (picked?.length) {
    const already = handshake.value && user.value ? photosInSlot(handshake.value, user.value.id, null) : 0;
    const batch = photosToUpload(picked, already);
    if (batch.length < picked.length) toast.error(localize('handshake.photo.limitHandshake'));
    chosenFiles.value = batch;
    previewUrl.value = batch[0] ? URL.createObjectURL(batch[0]) : undefined;
  } else {
    chosenFiles.value = [];
    previewUrl.value = undefined;
  }
});

// The handshake itself is a single small request, so waiting on it is a blink
// rather than a wait. Only a request that has genuinely stalled earns the
// loading state — anything sooner is a flicker.
const STALL_REVEAL_MS = 400;

const startSubmitting = () => {
  isSubmitting.value = true;
  stallTimer = setTimeout(() => (isStalled.value = true), STALL_REVEAL_MS);
};

const stopSubmitting = () => {
  if (stallTimer) clearTimeout(stallTimer);
  stallTimer = null;
  isStalled.value = false;
  isSubmitting.value = false;
};

// The photo is left out of both: it goes up on its own afterwards, so nobody
// stands in front of a spinner holding a phone while a picture uploads.
const submitOffer = async (written: string) => {
  return await gambits.userService.offerHandshake(displayPlayer.value!.id, undefined, {
    gambitId: gambitId.value || undefined,
    locationId: locationId.value || undefined,
    note: written || undefined,
  });
};

const submitResponse = async (written: string) => {
  return await gambits.userService.acceptHandshake(handshake.value!.id, undefined, written || undefined);
};

const handleSubmit = async () => {
  if (!user.value?.id || !displayPlayer.value?.id) return;
  if (handshake.value?.isConfirmed) {
    emit(GamComponentsEmits.CLOSE, { refresh: true });
    return;
  }
  if (isSubmitting.value || isDeleting.value) return;

  startSubmitting();
  actionTaken.value = true;

  const h = handshake.value;
  const photos = chosenFiles.value;
  // A note the handshake could not take yet goes with the offer or the accept,
  // so it is on record before anyone is told the handshake changed. A photo
  // still follows, being too large to ride along.
  const heldNote = canSaveNote.value ? '' : tidyHandshakeNote(note.value);
  const result = !h
    ? await submitOffer(heldNote)
    : h.responderId === user.value.id
      ? await submitResponse(heldNote)
      : null;

  stopSubmitting();

  if (result?.error) {
    const reason = result.error.message || 'Something went wrong.';
    toast.error(`Couldn't send handshake: ${reason}`);
    actionTaken.value = false;
    return;
  }

  if (result?.data) learnHandshakeTap();

  const handshakeId = result?.data?.id || h?.id;
  if (photos.length && handshakeId) {
    void uploadPhoto(handshakeId, photos);
  }

  const id = result?.data?.id || h?.id;
  const accepted =
    !!result?.data && !h?.isConfirmed && (result.data.isConfirmed || (!!h && h.responderId === user.value?.id));
  if (accepted) celebrateHandshake(id, fireConfetti);
  else if (result?.data?.isConfirmed) rememberHandshake(id);

  emit(GamComponentsEmits.CLOSE, { refresh: true });
};

const showCancelConfirm = ref(false);
const handleConfirmCancel = async () => {
  showCancelConfirm.value = false;
  if (isSubmitting.value || isDeleting.value) return;
  if (!handshake.value) return;

  actionTaken.value = true;
  isDeleting.value = true;
  await gambits.userService.deleteHandshake(handshake.value.id);
  isDeleting.value = false;

  emit(GamComponentsEmits.CLOSE, { refresh: true });
};

const handleDialogClose = async () => {
  if (actionTaken.value) return;
  emit(GamComponentsEmits.CLOSE);
};

const handleUpload = () => {
  openUpload();
};

const handleReset = () => {
  resetUpload();
};
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;

@keyframes breathe {
  0%,
  100% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.06);
  }
}

.message {
  padding: var(--spacing-xs) var(--spacing-l);
  font-size: 13px;
  letter-spacing: 0.05em;
  text-align: center;
  line-height: 1.8;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-md);
  user-select: none;
}

.line {
  display: flex;
  padding: var(--spacing-xxs) var(--spacing-l);
}

.lead-row {
  display: flex;
  align-items: center;
  gap: var(--spacing-s);
  white-space: nowrap;
}

.player-name {
  background: var(--color-dark-30);
  backdrop-filter: blur(15px);
  border-radius: 32px;
  font-weight: 700;
}

.handshake-button {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;
  user-select: none;
}

// The disc is the sheet's centrepiece, so it takes its size from the board it
// stands on rather than a fixed one: a square leaves less room above and below
// than the tall card this came off, and a phone leaves less again.
// Three taps and a rest, the finger and the disc on the same beat.
$tap-cycle: 3.6s;
$tap-delay: 0.8s;

// Raised like every button pressed at the board, and still: breathing read as
// something already under way. Only an offer left waiting keeps it.
.handshake-disc {
  display: flex;
  align-items: center;
  justify-content: center;
  width: min(160px, 40cqmin);
  aspect-ratio: 1;
  border-radius: 50%;
  background: var(--color-pink);
  @include raised-disc;

  &.is-waiting {
    animation: breathe 4s ease-in-out infinite;
  }

  &.is-knocking {
    animation: knock $tap-cycle ease-in-out $tap-delay infinite;
  }
}

.handshake-button:active .handshake-disc {
  animation: none;
  @include raised-disc-pressed;
}

@keyframes knock {
  0%,
  16%,
  23%,
  28%,
  35%,
  40%,
  47%,
  100% {
    transform: scale(1);
  }

  19%,
  31%,
  43% {
    transform: scale(0.965);
  }
}

// The fingertip lands a little below and right of the middle, so the
// handshake on the disc stays in sight under it.
.tap-hint {
  position: absolute;
  top: calc(60% - 14px);
  left: calc(60% - 20px);
  z-index: 2;
  display: flex;
  opacity: 0;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.45));
  transform-origin: 35% 25%;
  pointer-events: none;
  animation: tap-finger $tap-cycle ease-in-out $tap-delay infinite;
}

@keyframes tap-finger {
  0% {
    opacity: 0;
    transform: translate(10px, 10px);
  }

  10%,
  16%,
  23%,
  28%,
  35%,
  40%,
  47% {
    opacity: 0.95;
    transform: translate(0, 0) scale(1);
  }

  19%,
  31%,
  43% {
    opacity: 0.95;
    transform: translate(0, 3px) scale(0.88);
  }

  60%,
  100% {
    opacity: 0;
    transform: translate(10px, 10px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .handshake-disc.is-knocking {
    animation: none;
  }

  .tap-hint {
    opacity: 0.95;
    animation: none;
  }
}

// Ripples read as "broadcasting", so they only show while a handshake waits
// on the other player — never on the tappable offer/accept state.
.handshake-pulse {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 2px solid var(--color-pink);
  animation: handshake-pulse 2.4s ease-out infinite;
}

@keyframes handshake-pulse {
  0% {
    transform: scale(1);
    opacity: 0.5;
  }

  100% {
    transform: scale(1.35);
    opacity: 0;
  }
}

.handshake-disc-icon {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;

  :deep(svg *) {
    fill: var(--color-white) !important;
  }
}

/* The photo button of the recommendation's toolbar. A chosen photo is the small
   square in its place, and the board behind the sheet stays the board. */
.photo-slot {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
}

.note-slot {
  display: flex;
  min-width: 0;
}

.photo-slot,
.note-slot {
  transition: opacity var(--transition);

  &.is-busy {
    opacity: 0.4;
  }
}

.photo-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: var(--color-dark-500);
  color: var(--color-white-70);
  cursor: pointer;
  transition: background var(--transition);

  @include hover {
    background: linear-gradient(var(--color-white-8), var(--color-white-8)), var(--color-dark-500);
  }

  @include press;

  &.has-photo {
    width: 32px;
    height: 32px;
    overflow: hidden;
    border: 1px solid var(--color-white-100);
    border-radius: var(--radius-x-small);
    background: transparent;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      pointer-events: none;
      user-select: none;
    }
  }
}

.photo-remove {
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: var(--color-dark-900);
  box-shadow: inset 0 0 0 1px var(--color-white-20);
  color: var(--color-white);
  cursor: pointer;
}

.timestamp {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  font-size: 11px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  line-height: 15px;
  text-align: left;
  white-space: nowrap;
  color: var(--color-white-100);
}
</style>
